/* eslint-disable no-param-reassign */
/* global React */
import { useState, useEffect, forwardRef, useRef } from 'react'
import {
  Grid, Step as MuiStep, StepButton, StepContent, StepLabel, Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { arrayOf } from 'lib/util'
import { useMemoRef } from 'platform/react/hook'
import Section from 'ui/Element/Section'
import Button from 'ui/Element/Button'
import Icon from 'ui/Element/Icon'
import ErrorBoundary from 'ui/Error'

const ForwardedRefSection = forwardRef(Section)

/**
 * Returns the Step component's current theme styles.
 *
 * @param {Object} theme  the application's current theme
 * @returns {Object}      the Step component's styles
 */
const useStyles = makeStyles(theme => ({
  root: {
    justifyContent: 'space-between',
  },
  stepLabel: {
    maxWidth: '100%',
    padding: 0,
  },
  button: {
    width: 'auto',
    flex: [[0, 0, 'auto']],
    marginLeft: -8,
    maxWidth: '100%',
  },
  buttonLabel: {
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',

    '& > *': {
      maxWidth: '100%',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      textAlign: 'justify',
      lineClamp: 3,
      display: '-webkit-box',
      boxOrient: 'vertical',
      // whiteSpace: 'nowrap',
    },
  },
  stepContent: {
    width: ['100%', '!important'],
    maxWidth: '100%',
    borderColor: theme.palette.divider,
  },
  contentContainer: {
    maxHeight: '100%',
    margin: [[24, 0, 8]],
    maxWidth: '100%',
    width: ['100%', '!important'],
  },
  ...theme.custom.step,
}))

/**
 * Wraps the step `actions`.
 *
 * @param {Object[]} actions  a list of actions to show for the step
 * @returns {JSX.Element}
 */
const StepActions = ({ actions = [] }) => (
  <Grid
    item
    container
    component={'span'}
    justifyContent={'flex-end'}
    columnSpacing={2}
    xs={'auto'}
  >
    {actions
      .filter(action => !action.props.hidden)
      .map((action, index) => (
        <Grid
          key={index}
          item
        >
          {action}
        </Grid>
      ))
    }
  </Grid>
)

/**
 * Step skip button element.
 *
 * @param {boolean} hidden      whether to hide or display the button
 * @param {string} value        button label's text
 * @param onClick               a function to be called when the button is clicked
 * @returns {null|JSX.Element}
 */
const StepSkipButton = ({ hidden, value, onClick }) => (hidden ? null : (
  <Grid
    item
    component={'span'}
    justifyContent={'right'}
    sx={{ marginLeft: 'auto' }}
    xs={'auto'}
  >
    <Button
      value={value}
      variant={'text'}
      onClick={onClick}
      fullWidth={false}
      color={'primary'}
      endIcon={'arrow-circle-right'}
      xs={'auto'}
    />
  </Grid>
))

/**
 * Step component designed to be used as child for the {@link Step} and as a wrapper
 * for other elements.
 *
 * A step is in error state if it is required, visited and not confirmed.
 *
 * @param {Object} props                element's properties
 * @param {Object} props.forwardedRef     a forwarded reference to the root element
 * @param {boolean} props.index           the step's position in the stepper
 * @param {number} [props.activeStep]     the index of the currently active step
 * @param {Object[]} props.children       an array of elements to be used as the step content
 * @param {Object[]} [props.actions]      an array of elements to be used as the step actions
 * @param {function} props.onClick        handler executed whenever the step label is clicked
 * @param {boolean} [props.required]      whether the step has to be completed
 * @param {boolean} [props.confirmed]     whether the step has confirmed state
 * @param {boolean} [props.completed]     whether the step has completed state
 * @param {boolean} [props.visited]       whether the step has already been visited
 * @param {boolean} [props.skipped]       whether or not this step has been skipped
 * @param {number} [props.skipTo]         step to go to if this one is skipped
 * @param {string} [props.label]          a string to be used as step label
 * @param {string} [props.title]          a string to be used as step title
 * @param {string} [props.subtitle]       a string to be used as step subtitle
 * @param {Object} [props.error]          an object containing strings used in case of error
 * @param {string} [props.error.title]    a string to be used as step title in case of error
 * @param {string} [props.error.subtitle] a string to be used as step subtitle in case of error
 * @param {string} [props.skipTitle]      title to be displayed if step is skipped
 * @param {string} [props.skipButtonText] text to be displayed as skip button's label
 * @param {number} [props.spacing]        the number of spacings between the contained items
 * @param {number} [props.space]          the number of spacings above and below the contained items
 * @param {number} [props.gap]            the number of spacings at each side of the contained items
 * @param {string[]} [props.classes]      component's style definitions
 * @returns {ReactNode}
 */
const Step = ({
  forwardedRef,
  index: stepIndex,
  activeStep,
  children,
  actions,
  onClick,
  required,
  confirmed,
  completed,
  visited,
  skipped,
  skipTo,
  label,
  title: stepTitle,
  subtitle: stepSubtitle,
  error: {
    title: errorTitle,
    subtitle: errorSubtitle
  } = {},
  skipTitle,
  skipButtonText,
  gap,
  space,
  spacing,
  ...props
}) => {
  const classes = useStyles()
  const sectionRef = useRef()

  const [child] = arrayOf(children)

  const current = activeStep === stepIndex
  const [isVisited, setVisited] = useState(current || visited)
  const error = !completed && required && isVisited && !current
  const confirmError = completed && !confirmed && isVisited && !current
  const title = stepTitle || (error && errorTitle) || (skipped && skipTitle) || ''
  const subtitle = stepSubtitle || (!stepTitle && error && errorSubtitle) || ''

  useEffect(() => {
    setVisited(isVisited || visited)
  }, [visited])

  return (
    <Grid
      {...props}
      index={stepIndex}
      ref={forwardedRef}
      key={label}
      item
      container
      component={MuiStep}
      completed={completed}
      justifyContent={'space-between'}
      classes={{ root: classes.root }}
      xs={child.props.stepProps?.xs || props.xs}
      sm={child.props.stepProps?.sm || props.sm}
      md={child.props.stepProps?.md || props.md}
      lg={child.props.stepProps?.lg || props.lg}
      xl={child.props.stepProps?.xl || props.xl}
    >
      <Grid
        item
        container
        justifyContent={'space-between'}
      >
        <Grid
          item
          component={StepButton}
          className={classes.button}
          onClick={onClick(stepIndex)}
          optional={!title || current ? null : (
            <>
              <Typography
                variant={'caption'}
                color={error ? 'error' : 'body2'}
              >
                {title}
              </Typography>
              <Typography
                variant={'caption'}
                color={error ? 'error' : 'body2'}
              >
                {subtitle}
              </Typography>
            </>
          )}
        >
          <StepLabel
            error={error}
            StepIconProps={{
              ...completed && !confirmed && {
                icon: (
                  <Icon
                    name={'edit'}
                    color={confirmError ? 'error.contrastText' : 'warning.contrastText'}
                    background={confirmError ? 'error' : 'warning'}
                    iconPadding={0.6}
                    rounded={true}
                    size={'0.9rem'}
                  />
                ),
              },
            }}
            classes={{
              root: classes.stepLabel,
              labelContainer: classes.buttonLabel,
            }}
          >
            {label}
          </StepLabel>
        </Grid>
        <Grid
          item
          container
          xs={'auto'}
          justifyContent={'flex-end'}
          columnSpacing={2}
        >
          {current && (
            <StepActions
              actions={actions}
            />
          )}
          <Grid
            item
            component={StepSkipButton}
            value={skipButtonText}
            onClick={onClick(skipTo || stepIndex + 1, true)}
            hidden={required || !current || completed}
          />
        </Grid>
      </Grid>
      <Grid
        component={StepContent}
        className={classes.stepContent}
      >
        <ErrorBoundary>
          <ForwardedRefSection
            ref={sectionRef}
            spacing={spacing}
            gap={gap}
            space={space}
            classes={props.classes}
            className={classes.contentContainer}
          >
            {children}
          </ForwardedRefSection>
        </ErrorBoundary>
      </Grid>
    </Grid>
  )
}

export default useMemoRef(Step, false)
